import { useMemo, useCallback, useRef, useEffect } from "react"
import { generateLocalParts } from "~/react/components/email_accounts/utils"
import type { PreviewRow, ManualAccount } from "~/react/components/email_accounts/types"

interface UseAccountPreviewParams {
  manualMode: boolean
  manualAccounts: ManualAccount[]
  selectedSenderInfos: Array<{ id: string; first: string; last: string }>
  accountGenerationDomains: string[]
  emailAccountsPerSender: number
  signature: string
  preview: PreviewRow[]
  setPreview: React.Dispatch<React.SetStateAction<PreviewRow[]>>
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>
  existingEmails?: string[]
}

interface UseAccountPreviewReturn {
  buildPreview: () => void
  duplicateEmailMap: Record<string, number>
}

/**
 * Hook for managing account preview generation and duplicate detection.
 */
export const useAccountPreview = ({
  manualMode,
  manualAccounts,
  selectedSenderInfos,
  accountGenerationDomains,
  emailAccountsPerSender,
  signature,
  preview,
  setPreview,
  setShowPreview,
  existingEmails = [],
}: UseAccountPreviewParams): UseAccountPreviewReturn => {
  // Store timeout IDs to clean them up on unmount
  const timeoutIdsRef = useRef<number[]>([])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach((id) => {
        if (typeof window !== "undefined" && id) {
          clearTimeout(id)
        }
      })
      timeoutIdsRef.current = []
    }
  }, [])

  const normalizedExistingEmails = useMemo(() => {
    return existingEmails
      .map((email) => email?.trim().toLowerCase())
      .filter((email): email is string => Boolean(email) && email.includes("@"))
  }, [existingEmails])

  const duplicateEmailMap = useMemo(() => {
    const counts: Record<string, number> = {}

    normalizedExistingEmails.forEach((email) => {
      counts[email] = (counts[email] || 0) + 1
    })

    manualAccounts.forEach((a) => {
      const key = `${a.local.trim().toLowerCase()}@${(a.domain || "").trim().toLowerCase()}`
      if (!key.includes("@") || key.endsWith("@")) return
      counts[key] = (counts[key] || 0) + 1
    })

    preview.forEach((p) => {
      if (p.status === "ready" && p.local && p.domain) {
        const key = `${p.local.trim().toLowerCase()}@${p.domain.trim().toLowerCase()}`
        if (!key.includes("@") || key.endsWith("@")) return
        counts[key] = (counts[key] || 0) + 1
      }
    })

    return counts
  }, [manualAccounts, preview, normalizedExistingEmails])

  const buildPreview = useCallback(() => {
    const readyRows: Array<{
      id: string
      first: string
      last: string
      local: string
      domain: string
      signature: string
    }> = []
    if (manualMode) {
      manualAccounts.forEach((a, idx) => {
        const domain = a.domain
        if (!a.first.trim() || !a.local.trim() || !domain) return
        if (!accountGenerationDomains.includes(domain)) return
        readyRows.push({
          id: a.id || `${Date.now()}-${idx}`,
          first: a.first.trim(),
          last: a.last.trim(),
          local: a.local.trim(),
          domain,
          signature: a.signature || signature,
        })
      })
      if (readyRows.length === 0) return setPreview([])
    } else {
      if (selectedSenderInfos.length === 0 || accountGenerationDomains.length === 0) return setPreview([])

      const existingEmails = new Set<string>(normalizedExistingEmails)
      preview.forEach((p) => {
        if (p.status === "ready" && p.local && p.domain) {
          const email = `${p.local.toLowerCase().trim()}@${p.domain.toLowerCase().trim()}`
          existingEmails.add(email)
        }
      })

      const timestamp = Date.now()
      let counter = 1

      selectedSenderInfos.forEach((sender) => {
        const allPossibleLocals = generateLocalParts(sender.first, sender.last, 50)
        const allCombinations: Array<{ local: string; domain: string }> = []
        allPossibleLocals.forEach((local) => {
          accountGenerationDomains.forEach((domain) => {
            allCombinations.push({ local, domain })
          })
        })

        const availableCombinations = allCombinations.filter(({ local, domain }) => {
          const email = `${local.toLowerCase().trim()}@${domain.toLowerCase().trim()}`
          return !existingEmails.has(email)
        })

        const selectedCombinations = availableCombinations.slice(0, emailAccountsPerSender)

        if (selectedCombinations.length < emailAccountsPerSender) {
          let suffixCounter = 1
          const needed = emailAccountsPerSender - selectedCombinations.length

          for (let i = 0; i < needed && suffixCounter < 100; i++) {
            let found = false
            for (const local of allPossibleLocals) {
              for (const domain of accountGenerationDomains) {
                const email = `${local.toLowerCase().trim()}${suffixCounter}@${domain.toLowerCase().trim()}`
                if (!existingEmails.has(email)) {
                  selectedCombinations.push({
                    local: `${local}${suffixCounter}`,
                    domain,
                  })
                  existingEmails.add(email)
                  suffixCounter++
                  found = true
                  break
                }
              }
              if (found) break
            }
            if (!found) suffixCounter++
          }
        }

        selectedCombinations.forEach(({ local, domain }) => {
          readyRows.push({
            id: `${sender.id}-${domain}-${timestamp}-${counter++}`,
            first: sender.first,
            last: sender.last,
            local,
            domain,
            signature,
          })
        })
      })
    }

    const currentPreview = preview
    const existingEmails = new Set<string>()

    // Only block already-managed mailboxes when generating suggestions automatically
    if (!manualMode) {
      normalizedExistingEmails.forEach((email) => existingEmails.add(email))
    }

    currentPreview.forEach((p) => {
      if (p.status === "ready" && p.local && p.domain) {
        const email = `${p.local.toLowerCase().trim()}@${p.domain.toLowerCase().trim()}`
        existingEmails.add(email)
      } else if (p.status === "loading" && p.id) {
      }
    })

    const newEmails = new Set<string>()
    const uniqueReadyRows = readyRows.filter((row) => {
      const email = `${row.local.toLowerCase().trim()}@${row.domain.toLowerCase().trim()}`
      if (existingEmails.has(email) || newEmails.has(email)) {
        return false
      }
      newEmails.add(email)
      return true
    })

    if (uniqueReadyRows.length === 0) {
      return
    }

    const placeholders: PreviewRow[] = uniqueReadyRows.map((r) => ({
      id: r.id,
      status: "loading",
    }))
    setPreview((prev) => {
      const existingIds = new Set(prev.map((p) => p.id))
      const newPlaceholders = placeholders.filter((p) => !existingIds.has(p.id))
      return [...prev, ...newPlaceholders]
    })
    setShowPreview(true)

    // Replace progressively - only for unique rows
    // Clear any existing timeouts first
    timeoutIdsRef.current.forEach((id) => {
      if (typeof window !== "undefined" && id) {
        clearTimeout(id)
      }
    })
    timeoutIdsRef.current = []

    uniqueReadyRows.forEach((row, idx) => {
      if (typeof window === "undefined") return

      const timeoutId = window.setTimeout(
        () => {
          setPreview((prev) => {
            const next = [...prev]
            const i = next.findIndex((r) => r.id === row.id)
            if (i !== -1) {
              next[i] = { status: "ready", ...row }
            }
            return next
          })
        },
        300 + idx * 180,
      )
      timeoutIdsRef.current.push(timeoutId)
    })
  }, [
    manualMode,
    manualAccounts,
    selectedSenderInfos,
    accountGenerationDomains,
    emailAccountsPerSender,
    signature,
    preview,
    setPreview,
    setShowPreview,
    normalizedExistingEmails,
  ])

  return {
    buildPreview,
    duplicateEmailMap,
  }
}
